import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Modal, Form, Input, InputNumber, Select, Switch, Button, Space, Checkbox, message, Table } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Line } from "../../api";
import styled from "styled-components";

const { Option } = Select;

const StyledTable = styled(Table)`
  .ant-table-container {
    overflow-x: auto !important;
  }
  .ant-table-body {
    overflow-x: auto !important;
    min-width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto !important;
  }
`;

export default function LineModal({ visible, setVisible, tokens, groups, onRefreshGroups }) {
  const [data, setData] = useState([]);
  const [keyword, setKeyWord] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [onEdit, setOnEdit] = useState(false);
  const [onCreate, setOnCreate] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [isTableVisible, setIsTableVisible] = useState(false);

  useLayoutEffect(() => {
    if (isTableVisible) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isTableVisible]);

  useLayoutEffect(() => {
    if (visible) {
      const timer = setTimeout(() => {
        setIsTableVisible(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsTableVisible(false);
    }
  }, [visible]);

  const fetchData = async () => {
    if (!visible) return;
    const response = await Line.GetAllLineDataV2();
    if (response.success) {
      setData(response.data);
    } else {
      message.error("無法取得群組資料");
    }
  };

  useEffect(() => {
    fetchData();
  }, [visible]);

  const filteredData = data.filter((line) => {
    if (!showAll) {
      if (!line.is_line_msg && !line.is_tg) return false;
    }
    if (keyword === "") return true;
    if (line.name.search(new RegExp(keyword, "i")) !== -1) return true;
    if ((line.URL ?? "").search(keyword) !== -1) return true;
    if ((line.token_name ?? "").search(new RegExp(keyword, "i")) !== -1) return true;
    return false;
  });

  const DeleteLine = async () => {
    const { id } = form.getFieldsValue();
    const response = await Line.DeleteLineV2(id);
    if (response.success) {
      message.success("刪除成功");
      fetchData();
      setOnEdit(false);
      setEditModalVisible(false);
    } else {
      message.error("刪除失敗: " + response.error);
    }
  };

  const confirm = () => {
    Modal.confirm({
      title: "確認",
      content: "確認刪除此群組？",
      okText: "確認",
      cancelText: "取消",
      onOk: DeleteLine,
    });
  };

  const onSubmit = async () => {
    const values = form.getFieldsValue();
    console.log("===== 表單原始資料 =====");
    console.log(values);
    
    // Ensure warnings is an array
    if (!values.warnings) values.warnings = [];

    values.is_line_msg = values.is_line_msg ? 1 : 0;
    values.is_tg = values.is_tg ? 1 : 0;

    // 根據 create_new_group 的狀態決定要保留哪個欄位
    if (values.create_new_group) {
      // 如果是建立新群組，移除 token_id，保留 line_group_id
      delete values.token_id;
      console.log("===== 模式: 建立新群組 =====");
    } else {
      // 如果是選擇現有群組，移除 line_group_id，保留 token_id
      delete values.line_group_id;
      console.log("===== 模式: 選擇現有群組 =====");
    }
    // 移除輔助欄位，不送到後端
    delete values.create_new_group;

    console.log("===== 處理後將送出的資料 =====");
    console.log(values);
    console.log("=========================");

    // 暫時註解掉實際的 API 呼叫
    // return;

    if (onCreate) {
      const response = await Line.CreateLineV2(values);
      if (response.success) {
        message.success("新增成功");
        fetchData();
        if (onRefreshGroups) onRefreshGroups();
        setOnCreate(false);
        setEditModalVisible(false);
      } else {
        message.error("新增失敗: " + response.error);
      }
    } else if (onEdit) {
      const response = await Line.UpdateLineV2(values.id, values);
      if (response.success) {
        message.success("更新成功");
        fetchData();
        if (onRefreshGroups) onRefreshGroups();
        setOnEdit(false);
        setEditModalVisible(false);
      } else {
        message.error("更新失敗: " + response.error);
      }
    }
  };

  // Validation logic from old LineModal adapted for V2
  const handleTokenChange = (value) => {
    form.setFieldsValue({ URL: "" });
  };

  const handleURLChange = (e) => {
    form.setFieldsValue({ line_group_id: undefined });
  };

  const columns = [
    { title: "群組名稱", dataIndex: "name", width: 150 },
    {
      title: "Line 群組 ID",
      dataIndex: "line_group_id",
      width: 150,
      render: (text, record) => {
        let displayText = text;
        let textColor = "inherit";
        if (!text || text === "NULL") {
          // line_group_id 是空的
          if (record.is_line_msg && record.token_id) {
            displayText = `請設定 textgroup#${record.token_id}`;
            textColor = "#ff6b6b"; // 紅色提醒
          } else {
            displayText = "-";
          }
        } else if (!record.is_line_msg) {
          displayText = "-";
        }
        return (
          <div style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all", color: textColor }}>
            {displayText}
          </div>
        );
      },
    },
    ...(showDetails
      ? [
          {
            title: "Line Message",
            width: 100,
            render: (text, record) => (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Checkbox checked={record.is_line_msg} disabled />
              </div>
            ),
          },
        ]
      : []),
    {
      title: "Line Channel Token",
      dataIndex: "channel_token",
      width: 150,
      render: (text, record) => {
        const displayText = record.is_line_msg ? text : "";
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              width: "150px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={displayText}
          >
            {displayText}
          </div>
        );
      },
    },
    ...(showDetails
      ? [
          {
            title: "Telegram",
            width: 100,
            render: (text, record) => (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Checkbox checked={record.is_tg} disabled />
              </div>
            ),
          },
        ]
      : []),
    { title: "TG Group", dataIndex: "tg_groupid", width: 100 },
    ...(showDetails
      ? [
          {
            title: "訊息量方案",
            dataIndex: "plan_usage",
            width: 150,
            render: (value, record) => {
              const options = {
                0: "常規用量0-100則",
                1: "增額用量101-300則",
                2: "高額用量301-500則",
              };
              return options[value] || value;
            },
          },
          {
            title: "人數方案",
            dataIndex: "plan_members",
            width: 150,
            render: (value, record) => {
              const options = {
                0: "5-10人(中小型群組)",
                1: "11-50人(中型群組)",
                2: "51-100人(大型群組)",
              };
              return options[value] || value;
            },
          },
        ]
      : []),
    { title: "訊息量", dataIndex: "usage", width: 80 },
    ...(showDetails ? [{ title: "人數", dataIndex: "members", width: 80 }] : []),
    {
      title: "動作",
      width: 100,
      render: (text, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            onClick={() => {
              setOnEdit(true);
              setOnCreate(false);
              // 確保 ID 欄位為字串型別以匹配 Select 的 Option value
              const editData = {
                ...record,
                token_id: record.token_id ? String(record.token_id) : undefined,
                // 只有在有 token_id 時才設置 create_new_group 為 false，否則為 true
                create_new_group: record.token_id ? false : (record.line_group_id ? true : false),
                // 當 create_new_group 為 true 時，不填入 line_group_id
                line_group_id: undefined,
              };
              
              // 如果 is_line_msg 是 false，清空相關欄位並設置默認值
              if (!record.is_line_msg) {
                editData.channel_id = "";
                editData.channel_secret = "";
                editData.channel_token = "";
                editData.plan_usage = 0;
                editData.plan_members = 0;
                editData.usage = 0;
                editData.members = 0;
                editData.warnings = [
                  { threshold: 50, message: `訊息量已達 50%,欲購買流量請洽本公司02-7751-5063。` },
                  { threshold: 80, message: `訊息量已達 80%,欲購買流量請洽本公司02-7751-5063。` },
                ];
              }
              
              form.setFieldsValue(editData);
              setEditModalVisible(true);
            }}
          >
            編輯
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="管理通報群組"
      forceRender
      open={visible}
      width={1500}
      style={{ overflowX: "hidden" }}
      onCancel={() => setVisible(false)}
      footer={[
        <Button onClick={() => setVisible(false)} key="back-button">
          返回
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 8 }}>顯示全部群組:</span>
          <Switch checked={showAll} onChange={setShowAll} />
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 8 }}>顯示詳細資訊:</span>
          <Switch checked={showDetails} onChange={setShowDetails} />
        </div>
        <Input style={{ width: 200 }} placeholder="輸入關鍵字" onChange={(e) => setKeyWord(e.target.value)} />
        <Button
          style={{ minWidth: 80 }}
          onClick={() => {
            if (keyword === "") {
              return;
            }
            // Filter logic is already handled in filteredData
          }}
        >
          查詢
        </Button>
      </div>

      <div style={{ overflowX: "auto", overflowY: "hidden", marginBottom: "20px" }}>
        <div style={{ width: "100%", overflowX: "auto" }}>
          {isTableVisible && (
            <StyledTable
              rowKey="id"
              className="hide-column"
              dataSource={filteredData}
              columns={columns}
              pagination={{ position: ["bottomCenter"] }}
              scroll={{ x: "max-content" }}
            />
          )}
        </div>
      </div>

      <Button
        type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }}
        onClick={() => {
          setOnCreate(true);
          setOnEdit(false);
          form.resetFields();
          setEditModalVisible(true);
        }}
      >
        新增群組
      </Button>

      <Modal
        title={onEdit ? "編輯群組" : "新增群組"}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setOnEdit(false);
          setOnCreate(false);
        }}
        footer={null}
        width={900}
      >
        <Form form={form} onFinish={onSubmit} layout="vertical">
          <Form.Item name="id" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item name="name" label="群組名稱" rules={[{ required: true, message: "請輸入群組名稱" }]}>
            <Input />
          </Form.Item>

        <Form.Item name="is_line_msg" label="是否為Line Message" valuePropName="checked">
          <Switch onChange={(checked) => {
            if (checked) {
              // 當切換為 true 時，設置默認值
              const currentValues = form.getFieldsValue();
              
              // 如果 token_id 不存在或為 undefined，設置為 "0"
              if (!currentValues.token_id) {
                form.setFieldsValue({ token_id: "0" });
              }
              
              // 如果 warnings 不是數組或為空，設置默認值
              if (!Array.isArray(currentValues.warnings) || currentValues.warnings.length === 0) {
                form.setFieldsValue({
                  warnings: [
                    { threshold: 50, message: `訊息量已達 50%,欲購買流量請洽本公司02-7751-5063。` },
                    { threshold: 80, message: `訊息量已達 80%,欲購買流量請洽本公司02-7751-5063。` },
                  ]
                });
              }
            }
          }} />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.is_line_msg !== currentValues.is_line_msg}>
          {({ getFieldValue }) => {
            return getFieldValue("is_line_msg") ? (
              <>
                <Form.Item name="channel_id" label="Channel ID" rules={[{ required: true, message: "This field is required" }]}>
                  <Input placeholder="請輸入channel id" />
                </Form.Item>
                <Form.Item name="channel_secret" label="Channel Secret" rules={[{ required: true, message: "This field is required" }]}>
                  <Input placeholder="請輸入channel secret" />
                </Form.Item>
                <Form.Item name="channel_token" label="Channel Access Token" rules={[{ required: true, message: "This field is required" }]}>
                  <Input placeholder="請輸入channel access token" />
                </Form.Item>
                <Form.Item name="create_new_group" label="建立新群組" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.create_new_group !== currentValues.create_new_group}>
                  {({ getFieldValue }) => {
                    return getFieldValue("create_new_group") ? (
                      <Form.Item name="line_group_id" label="Line Channel ID (選填)">
                        <Input placeholder="請輸入Line Channel ID" />
                      </Form.Item>
                    ) : (
                      <Form.Item name="token_id" label="選擇群組">
                        <Select
                          placeholder="選擇群組"
                          style={{ width: 300 }}
                          allowClear
                        >
                          <Option value="0">無群組</Option>
                          {groups.map((group) => (
                            <Option key={String(group.id)} value={String(group.id)}>
                              {group.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>

                <Form.Item name="plan_usage" label="訊息量方案" rules={[{ required: false, message: "請選擇訊息量" }]}>
                  <Select
                    value={getFieldValue("plan_usage")}
                    placeholder="請選擇訊息量"
                    onChange={(value) => {
                      const warnings = form.getFieldValue("warnings") || [];
                      const updatedWarnings = warnings.map((warning) => ({
                        ...warning,
                        message: `訊息量已達 ${warning.threshold}%,欲購買流量請洽本公司02-7751-5063。`,
                      }));
                      form.setFieldsValue({ warnings: updatedWarnings });
                    }}
                  >
                    <Option value={0}>常規用量0-100則</Option>
                    <Option value={1}>增額用量101-300則</Option>
                    <Option value={2}>高額用量301-500則</Option>
                  </Select>
                </Form.Item>

                <Form.Item name="plan_members" label="人數方案" rules={[{ required: false, message: "請選擇人數" }]}>
                  <Select value={getFieldValue("plan_members")} placeholder="請選擇人數">
                    <Option value={0}>5-10人(中小型群組)</Option>
                    <Option value={1}>11-50人(中型群組)</Option>
                    <Option value={2}>51-100人(大型群組)</Option>
                  </Select>
                </Form.Item>

                <Form.Item label="告警設置" required tooltip="設置不同百分比的告警閾值及對應訊息">
                  <Form.List
                    name="warnings"
                    initialValue={[
                      { threshold: 50, message: `訊息量已達 50%,欲購買流量請洽本公司02-7751-5063。` },
                      { threshold: 80, message: `訊息量已達 80%,欲購買流量請洽本公司02-7751-5063。` },
                    ]}
                  >
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                            <Form.Item {...restField} name={[name, "threshold"]} rules={[{ required: true, message: "請輸入百分比" }]}>
                              <InputNumber min={1} max={100} addonAfter="%" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "message"]}
                              rules={[{ required: true, message: "請輸入告警訊息" }]}
                              style={{ width: 400 }}
                            >
                              <Input placeholder="告警訊息" />
                            </Form.Item>
                            <Button onClick={() => remove(name)} type="text" danger icon={<MinusCircleOutlined />} />
                          </Space>
                        ))}
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                          添加告警設置
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Form.Item>

                <Form.Item name="usage" label="訊息量" initialValue={0}>
                  <InputNumber min={0} />
                </Form.Item>

                <Form.Item name="members" label="人數" initialValue={0}>
                  <InputNumber min={0} />
                </Form.Item>
              </>
            ) : null;
          }}
        </Form.Item>

        <Form.Item name="is_tg" label="是否為Telegram" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.is_tg !== currentValues.is_tg}>
          {({ getFieldValue }) => {
            return getFieldValue("is_tg") ? (
              <Form.Item name="tg_groupid" label="TG Group ID" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>

          <Space>
            <Button type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }} onClick={() => form.submit()}>
              {onEdit ? "確認" : "新增"}
            </Button>
            <Button danger hidden={!onEdit} onClick={confirm}>
              刪除
            </Button>
            <Button
              onClick={() => {
                setEditModalVisible(false);
                setOnCreate(false);
                setOnEdit(false);
              }}
            >
              取消
            </Button>
          </Space>
        </Form>
      </Modal>
    </Modal>
  );
}
